import { CustomAttr } from "@turizi/shared";
import ActionsController from "@/lib/actions-controller";
import { CloseButton, Popover } from "@mantine/core";
import { useState, useEffect } from "react";
import { formatDate, formatDay, formatToLocalTime, formatToLocalDate } from "@/lib/format-utils";
import { IconLoader2, IconCheck, IconEye } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";


interface ShowSelfAttrProps {
    attr: CustomAttr;
    context?: any;
    size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
    showkey?: boolean;
}

export default function ShowSelfAttr({ attr, context, size = 'sm', showkey = true }: ShowSelfAttrProps) {
    const { t } = useTranslation();
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [opened, setOpened] = useState<boolean>(false);

    // "smart" is a UI helper
    // the "action" is set in the backend
    const executeAction = async () => {
        if (attr.action) {
            setLoading(true);
            setError(null);
            try {
                const res = await ActionsController.execute(attr.action, attr.value, context);
                setData(res?.data);
                setError(res?.error);
            } catch (err: any) {
                setError(err?.message || t('An error occurred'));
            } finally {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        if (opened) {
            executeAction();
        }
    }, [opened]);

    const renderLoadingState = () => (
        <div className="flex flex-col items-center justify-center py-4 px-6 min-w-[200px] gap-2 animate-pulse">
            <IconLoader2 className="animate-spin text-blue-500" size={20} />
            <span className="text-xxs text-gray-500 font-medium">{t('Checking flight status...')}</span>
        </div>
    );

    const renderErrorState = (msg: string) => (
        <div className="text-xxs text-red-500 font-semibold p-3 flex items-center gap-1.5 min-w-[200px]">
            <span>⚠</span>
            <span>{msg || t('An error occurred')}</span>
        </div>
    );

    const renderFlightStatus = (info: any) => {
        if (!info || !info.flightNumber || !info.flights || info.flights.length === 0) {
            return (
                <div className="text-gray-500 text-xxs font-medium p-3 min-w-[200px]">
                    {t('Could not retrieve flight information')}
                </div>
            );
        }

        return (
            <div className="flex flex-col p-3 min-w-[280px] max-w-[340px] max-h-[350px] overflow-y-auto text-gray-800 font-sans gap-3">
                {/* Header */}
                <div className="flex justify-between items-center border-b border-gray-100 pb-1.5">
                    <span className="font-bold text-xs text-blue-600">{info.flightNumber}</span>
                    {info.flights.length === 1 && info.flights[0]?.fa_flight_id ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title={info.flights[0].fa_flight_id}>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            {t('tracking_active')}
                        </span>
                    ) : (
                        <span className="text-[10px] text-gray-400 font-medium">
                            {info.flights.length} {info.flights.length === 1 ? t('flight') : t('flights')}
                        </span>
                    )}
                </div>

                {/* Flights List */}
                <div className="flex flex-col gap-3">
                    {info.flights.map((flight: any, idx: number) => {
                        const originCode = flight.origin?.code_iata || (flight.origin?.code && (flight.origin.code.length === 4 ? flight.origin.code.substring(1) : flight.origin.code)) || '';
                        const destCode = flight.destination?.code_iata || (flight.destination?.code && (flight.destination.code.length === 4 ? flight.destination.code.substring(1) : flight.destination.code)) || '';
                        const depTimeIso = flight.scheduled_out || flight.scheduled_off || flight.estimated_out || flight.actual_out;
                        const arrTimeIso = flight.scheduled_in || flight.scheduled_on || flight.estimated_in || flight.actual_in;

                        return (
                            <div key={idx} className="flex flex-col bg-gray-50/50 border border-gray-100 p-2.5 rounded-lg">
                                {originCode && destCode ? (
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="text-left min-w-[80px]">
                                            <div className="font-extrabold text-sm text-gray-900 leading-none">{originCode}</div>
                                            <div className="text-[8px] text-gray-400 mt-0.5 uppercase tracking-wider">{t('Departure')}</div>
                                            <div className="font-bold text-xxs text-gray-700 mt-0.5">{formatToLocalTime(depTimeIso, flight.origin?.timezone)}</div>
                                            <div className="text-[9px] text-gray-500 mt-0.5">{formatDay(formatToLocalDate(depTimeIso, flight.origin?.timezone))}</div>
                                        </div>

                                        {/* Visual route line */}
                                        <div className="flex-1 px-2 flex items-center justify-center relative">
                                            <div className="h-[1px] w-full bg-gray-150 absolute top-1/2 left-0 -translate-y-1/2"></div>
                                            <div className="bg-white px-1 z-10 text-blue-500 text-[10px] transform rotate-90">
                                                ✈
                                            </div>
                                        </div>

                                        <div className="text-right min-w-[80px]">
                                            <div className="font-extrabold text-sm text-gray-900 leading-none">{destCode}</div>
                                            <div className="text-[8px] text-gray-400 mt-0.5 uppercase tracking-wider">{t('Arrival')}</div>
                                            <div className="font-bold text-xxs text-gray-700 mt-0.5">{formatToLocalTime(arrTimeIso, flight.destination?.timezone)}</div>
                                            <div className="text-[9px] text-gray-500 mt-0.5">{formatDay(formatToLocalDate(arrTimeIso, flight.destination?.timezone))}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-1 my-1 text-xxs">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400 font-medium">{t('Departure')}:</span>
                                            <span className="font-bold text-gray-700">
                                                {formatToLocalTime(depTimeIso, flight.origin?.timezone)}
                                                {depTimeIso && ` (${formatDay(formatToLocalDate(depTimeIso, flight.origin?.timezone))})`}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400 font-medium">{t('Arrival')}:</span>
                                            <span className="font-bold text-gray-700">
                                                {formatToLocalTime(arrTimeIso, flight.destination?.timezone)}
                                                {arrTimeIso && ` (${formatDay(formatToLocalDate(arrTimeIso, flight.destination?.timezone))})`}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // dont show empty attributes
    if (!attr || !attr?.value) {
        return null;
    }

    return (
        <Popover
            withArrow
            key={attr.key}
            width={'max-content'}
            position="top"
            shadow="xl"
            opened={opened}
        >
            <Popover.Target>
                <div className={`
                        w-fit
                        px-1 py-0.5
                        rounded-md
                        bg-gray-200
                        border border-gray-300
                        text-left
                        text-${size}
                        max-w-[300px]
                        ${attr.action ? 'cursor-pointer' : ''}
                    `}
                    key={attr.key}
                    onClick={(e) => {
                        if (attr.action) {
                            e.stopPropagation();
                            setOpened(!opened);
                        }
                    }}
                >
                    {
                        showkey && (
                            <div className={`
                                    text-xxs
                                    flex gap-1
                                    ${attr.action ? 'text-blue-500' : 'text-gray-400'}
                                `}
                            >
                                {t(attr.key?.split('#')?.[0] || attr.key)}
                                {attr.key.split('#')?.[1] && (
                                    <span className="font-bold">
                                        #{attr.key.split('#')?.[1]}
                                    </span>
                                )}
                            </div>
                        )
                    }
                    <div className="text-gray-900 break-words font-medium">
                        {attr.type === 'date' && formatDate(attr.value)}
                        {attr.type === 'boolean' && (attr.value ? t('yes') : t('no'))}
                        {attr.type === 'number' && attr.value}
                        {attr.type === 'text' && attr.value}
                    </div>

                </div>
            </Popover.Target>
            <Popover.Dropdown onClick={(e) => e.stopPropagation()} className="p-0 border border-gray-200 rounded-lg overflow-hidden">
                {attr.action && (
                    <div className="relative">
                        {loading && renderLoadingState()}
                        {!loading && error && renderErrorState(error)}
                        {!loading && !error && (
                            <div className="relative pr-5">
                                {attr.action === 'flight-check' ? renderFlightStatus(data) : (
                                    <div className="text-xxs p-3 font-medium flex items-center gap-1.5 text-gray-700 min-w-[200px]">
                                        <IconCheck size={14} className="text-green-500" />
                                        <span>{typeof data === 'object' && data?.message ? data.message : String(data || '')}</span>
                                    </div>
                                )}
                                <div className="absolute right-1 top-1">
                                    <CloseButton
                                        size="xs"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpened(false);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Popover.Dropdown>
        </Popover>
    );
}